import * as React from 'react';
import { useEffect, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { randUuid } from '../../../utils/functions';
import LayoutManager from '../../../utils/layout-manager';

/**
 * Setup a context menu for a button
 * @param {ContextMenuOptions} options
 * @constructor
 */
function ContextMenu(options) {
    const uniqId = `context-menu-${randUuid()}`;
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeMenuItemIndex, setActiveMenuItemIndex] = useState(null);

    useEffect(() => {
        const menuContainer = document.getElementById(uniqId);

        if (menuContainer === null) {
            return;
        }

        menuContainer.addEventListener('click', () => {
            setAnchorEl(menuContainer);
            menuContainer.parentElement.classList.add('selected');
        });
    }, []);

    const closeMenu = () => {
        const menuContainer = document.getElementById(uniqId);
        menuContainer.parentElement.classList.remove('selected');
        setActiveMenuItemIndex(null);
        setAnchorEl(null);
    };

    let anchorOriginData = { vertical: 'center', horizontal: 'center' };
    let transformOriginData = { vertical: 'center', horizontal: 'center' };

    let position = options.position;

    // make sure that the direction is enforced properly
    if (LayoutManager.getDirection() === 'rtl') {
        if (options.position === 'left') {
            position = 'right';
        } else if (options.position === 'right') {
            position = 'left';
        } else {
            // nothing to do
        }
    }

    switch (position) {
        case 'top':
            anchorOriginData = { vertical: 'top', horizontal: 'center' };
            transformOriginData = { vertical: 'bottom', horizontal: 'center' };
            break;

        case 'bottom':
            anchorOriginData = { vertical: 'bottom', horizontal: 'center' };
            transformOriginData = { vertical: 'top', horizontal: 'center' };
            break;

        case 'left':
            anchorOriginData = { vertical: 'center', horizontal: 'left' };
            transformOriginData = { vertical: 'center', horizontal: 'right' };
            break;

        case 'right':
            anchorOriginData = { vertical: 'center', horizontal: 'right' };
            transformOriginData = { vertical: 'center', horizontal: 'left' };
            break;
    }

    return (
        <>
            <div id={uniqId} className="context-menu">
                {options.trigger}
            </div>
            <Menu
                anchorEl={anchorEl}
                open={anchorEl !== null}
                onClose={closeMenu}
                onClick={closeMenu}
                disableAutoFocusItem={true}
                slotProps={{
                    paper: {
                        className: `context-menu`,
                        elevation: 0,
                    },
                }}
                transformOrigin={transformOriginData}
                anchorOrigin={anchorOriginData}
                data-test-id="context-menu"
            >
                {options.items.map((menuItem, index) => (
                    <MenuItem
                        variant="text"
                        onClick={() => menuItem.callback()}
                        key={`menu-item-${randUuid()}`}
                        selected={false}
                        onMouseEnter={() => {
                            if (!anchorEl) {
                                return;
                            }
                            setActiveMenuItemIndex(index);
                        }}
                        onMouseLeave={() => setActiveMenuItemIndex(null)}
                    >
                        <div className="context-menu-item" data-test-id={`context-menu-item-${menuItem.feature}`}>
                            <span className={`item-icon ${menuItem.glyphFontIcon}`}></span>
                            <span className={'item-text'}>{menuItem.text}</span>
                        </div>
                        {menuItem.subMenu && activeMenuItemIndex === index && (
                            <div className={`sub-menu ${menuItem.subMenu.className || ''}`}>
                                {menuItem.subMenu.items.map((subMenuItem) => (
                                    <div
                                        className="context-menu-item"
                                        key={`sub-menu-item-${randUuid()}`}
                                        onClick={() => subMenuItem.callback()}
                                    >
                                        <span className={`item-icon ${subMenuItem.glyphFontIcon}`}></span>
                                        <span className={'item-text'}>{subMenuItem.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default ContextMenu;
